import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const RecordingList = ({ recordings }) => {
  const { t } = useLanguage();

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    // Remove any non-digit characters except +
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    return cleaned;
  };

  const getWhatsAppLink = (phoneNumber) => {
    const formatted = formatPhoneNumber(phoneNumber);
    return `https://wa.me/${formatted.startsWith('+') ? formatted.slice(1) : formatted}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'skipped':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'processed':
        return t('recordings.status.processed', 'Processed');
      case 'pending':
        return t('recordings.status.pending', 'Pending');
      case 'skipped':
        return t('recordings.status.skipped', 'Skipped');
      default:
        return status;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('recordings.table.caller', 'Caller')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('recordings.table.recipient', 'Recipient')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('recordings.table.transcript', 'Transcript')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('recordings.table.date', 'Date')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('recordings.table.duration', 'Duration')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('recordings.table.status', 'Status')}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('recordings.table.actions', 'Actions')}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {recordings.map((recording) => (
            <tr key={recording.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  <a
                    href={getWhatsAppLink(recording.from_number)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-800 hover:underline"
                  >
                    {recording.from_number}
                  </a>
                </div>
                {recording.profile_name && (
                  <div className="text-xs text-gray-500">
                    {recording.profile_name}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  <a
                    href={getWhatsAppLink(recording.to_number)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-800 hover:underline"
                  >
                    {recording.to_number}
                  </a>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 max-w-xs truncate">
                  {recording.message_content || t('recordings.noTranscript', 'No transcript available')}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{formatDate(recording.timestamp)}</div>
                <div className="text-xs text-gray-500">{formatTime(recording.timestamp)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500">
                  {recording.duration || t('recordings.noDuration', 'Unknown')}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(recording.status)}`}>
                  {getStatusText(recording.status)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <Link
                  to={`/recordings/${recording.id}`}
                  className="text-blue-600 hover:text-blue-900 mr-4"
                >
                  {t('recordings.actions.view', 'View')}
                </Link>
                {recording.media_url && (
                  <a
                    href={"https://m.nexicare.ai/tr/" + recording.id}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-900"
                  >
                    {t('recordings.actions.listen', 'Listen')}
                  </a>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RecordingList;
